import type { MeResponse, OpsCapability, StaffRole } from '@ucp/web/lib';

/**
 * Operational issues roles per capability (PRO-3100, ADR-018 PRO-3100 amendment). Mirrors the
 * server role → capability map in `apps/api/src/auth/capabilities.ts` (asserted by
 * `apps/api/test/opsAccessModel.test.ts`). UX only: the server re-checks every call at the
 * record's school.
 */
export const OPS_CAPABILITY_ROLES: Readonly<Record<OpsCapability, readonly StaffRole[]>> = {
  ops_issue_read: ['OPS_SITE_LEADER', 'OPS_SITE_CONTRIBUTOR', 'OPS_DIRECTOR'],
  ops_issue_raise: ['OPS_SITE_LEADER', 'OPS_SITE_CONTRIBUTOR', 'OPS_DIRECTOR'],
  ops_issue_update: ['OPS_SITE_LEADER', 'OPS_SITE_CONTRIBUTOR', 'OPS_DIRECTOR'],
  ops_issue_propose_resolution: ['OPS_SITE_LEADER', 'OPS_DIRECTOR'],
  ops_issue_confirm_resolution: ['OPS_DIRECTOR'],
  ops_issue_confirm_group: ['COO', 'CEO'],
  ops_issue_reopen: ['OPS_SITE_LEADER', 'OPS_DIRECTOR'],
  ops_issue_export: ['OPS_SITE_LEADER', 'OPS_DIRECTOR'],
  ops_action_manage: ['OPS_SITE_LEADER', 'OPS_SITE_CONTRIBUTOR', 'OPS_DIRECTOR'],
  ops_action_cancel: ['OPS_SITE_LEADER'],
  ops_action_own: [
    'OPS_SITE_LEADER',
    'OPS_SITE_CONTRIBUTOR',
    'OPS_ACTION_OWNER',
    'OPS_DIRECTOR',
    'COO',
    'CEO',
  ],
  ops_issue_escalate: ['OPS_SITE_LEADER'],
  ops_escalation_respond: ['OPS_DIRECTOR'],
  ops_escalation_respond_group: ['COO', 'CEO'],
  ops_issue_read_portfolio: ['OPS_DIRECTOR'],
  ops_issue_read_group: ['COO', 'CEO', 'OPS_GROUP_VIEWER'],
  ops_taxonomy_admin: ['OPS_TAXONOMY_ADMIN'],
  // PRO-3140 / ADR-084 A2 — reaches the issue page to confirm Ofsted-origin issues only, so the
  // QA lead joins OPS_ROUTE_ROLES but holds no Weekly Report task role.
  ops_issue_confirm_ofsted: ['OFSTED_QA_LEAD'],
};

/** The site list: issue read at a school row. */
export const OPS_SITE_ROLES = OPS_CAPABILITY_ROLES.ops_issue_read;
export const OPS_PORTFOLIO_ROLES = OPS_CAPABILITY_ROLES.ops_issue_read_portfolio;
export const OPS_GROUP_ROLES = OPS_CAPABILITY_ROLES.ops_issue_read_group;
export const OPS_TAXONOMY_ROLES = OPS_CAPABILITY_ROLES.ops_taxonomy_admin;
export const OPS_ACTION_OWN_ROLES = OPS_CAPABILITY_ROLES.ops_action_own;

/** Every role with an ops capability; the `/weekly-report/*` RouteGuard. Never L6 (EoP). */
export const OPS_ROUTE_ROLES: readonly StaffRole[] = [
  ...new Set<StaffRole>(Object.values(OPS_CAPABILITY_ROLES).flat()),
];

/**
 * The ADR-014 A16 predicate for the shell-level My actions link: the module flag is on and the
 * caller holds a role carrying `ops_action_own`. Equals the `my-actions` task's own test.
 */
export function showMyActions(user: Pick<MeResponse, 'roles' | 'featureFlags'>): boolean {
  return (
    user.featureFlags.opsIssuesEnabled === true &&
    user.roles.some((r) => OPS_ACTION_OWN_ROLES.includes(r.role))
  );
}
